import { useStore } from "vuex";
import { parseJWT } from "@/utils/jwt";
import { IS_AUTH_BUILD_TARGET } from "@/config/index.js";
import { useFlags } from "@/flags/useFlags";

const EXPERIMENTAL_ROUTES = {
  "settings.tenant.emails": "customizableEmails",
};

// Route names that require a specific permission for ROLE_INSTITUTIONS_ADMIN.
// Routes absent from this map are accessible to all InstitutionsAdmins (e.g. institutions, announcements).
// ROLE_ADMIN users bypass this check entirely.
const INSTITUTIONS_ADMIN_ROUTE_PERMISSIONS = {
  // Dashboard
  dashboard: "dashboard",

  // Reports
  "orders.mealsToRelease": "reports_view",
  "orders.nestedMealsToRelease": "reports_view",
  "orders.periodicReport": "reports_view",
  "orders.bulkOrdersReport": "orders_view",
  "users.presences": "reports_view",
  mealsToPrepare: "meals_to_prepare_report",
  "orders.meals": "ordered_meals_report",
  "inventory.ingredients.purchaseReport": "purchase_report",
  "inventory.ingredients.foodCostReport": "food_cost_report",
  "inventory.ingredients.productionReport": "production_report",
  "transactions.refundReport": "reports_view",

  // Orders
  "calendar.subjects": "calendar_view",
  "orders.list": "orders_view",
  "orders.create": "orders_view",
  "orders.details": "orders_view",
  "orders.edit": "orders_view",
  "orders.labels": "orders_labels_view",
  terminal: "terminal_view",

  // Parents / Children
  "parents.list": "parents_view",
  "parents.create": "parents_view",
  "parents.details": "parents_view",
  "parents.edit": "parents_view",
  "parents.children.list": "parents_view",
  // Reachable directly from the Children list (children_view/children_edit), not only via a
  // parent's own children list (parents_view) - either permission must grant access.
  "parents.children.create": ["parents_view", "children_edit"],
  "parents.children.edit": ["parents_view", "children_edit"],
  "children.list": "children_view",
  "children.import": "children_view",
  "institutionsWorkers.list": "institution_workers_view",
  "institutionsWorkers.details": "institution_workers_view",
  "institutionsWorkers.create": "institution_workers_edit",
  "institutionsWorkers.edit": "institution_workers_edit",
  "institutionsWorkersGroups.list": "institution_workers_view",

  // Payments
  transactions: "transactions_view",
  "transactions.usersTransactions": "transactions_view",
  "transactions.bulkIncreaseBalances": "transactions_view",
  "transactions.deposits": "deposits_view",
  "transactions.charges": "deposits_view",
  "invoicesFiles.list": "invoices_view",
  "invoicesFiles.create": "invoices_view",
  "transactions.monthlyBalanceReport": "monthly_balance_report",
  "transactions.importedPaymentsFiles": "payments_import",
  "transactions.importedPaymentsFile": "payments_import",

  // Menu
  "mealsMenus.list": "menu_view",
  "mealsMenus.create": "menu_view",
  "mealsMenus.edit": "menu_view",
  "mealsMenus.importToExisting": "menu_view",
  "mealsMenus.import": "menu_view",
  "mealsMenus.prints": "menu_view",
  "priceLists.list": "menu_view",
  "priceLists.create": "menu_view",
  "priceLists.edit": "menu_view",
  "priceListsGroups.list": "menu_view",
  "priceListsGroups.create": "menu_view",
  "priceListsGroups.edit": "menu_view",
  "mealsMenusFiles.list": "menu_edit",
  "mealsMenusFiles.edit": "menu_edit",
  "mealsMenusFiles.create": "menu_edit",
  "meals.list": ["meals_view", "meals_edit"],
  "meals.create": "meals_edit",
  "meals.edit": "meals_edit",
  "recipes.list": ["recipes_view", "recipes_edit"],
  "recipes.create": "recipes_edit",
  "recipes.edit": "recipes_edit",
  "inventory.ingredients.list": ["ingredients_view", "ingredients_edit"],
  "inventory.ingredients.create": "ingredients_edit",
  "inventory.ingredients.edit": "ingredients_edit",
  "inventory.ingredients.import": "ingredients_edit",
  "inventory.ingredientCategories.list": [
    "ingredient_categories_view",
    "ingredient_categories_edit",
  ],
  "inventory.ingredientCategories.create": "ingredient_categories_edit",
  "inventory.ingredientCategories.edit": "ingredient_categories_edit",
  "allergens.list": ["allergens_view", "allergens_edit"],
  "allergens.create": "allergens_edit",
  "allergens.edit": "allergens_edit",
  "allergensGroups.list": ["allergens_groups_view", "allergens_groups_edit"],
  "allergensGroups.create": "allergens_groups_edit",
  "allergensGroups.edit": "allergens_groups_edit",

  "kitchens.list": ["kitchens_view", "kitchens_edit"],
  "kitchens.create": "kitchens_edit",
  "kitchens.edit": "kitchens_edit",
  "inventory.warehouse.list": "warehouse_view",
  "inventory.dailyStockOutcomes": ["daily_stock_outcomes_view", "daily_stock_outcomes_edit"],
  "inventory.incomes.list": ["inventory_incomes_view", "inventory_incomes_edit"],
  "inventory.incomes.details": ["inventory_incomes_view", "inventory_incomes_edit"],
  "inventory.incomes.create": "inventory_incomes_edit",
  "inventory.incomes.edit": "inventory_incomes_edit",
  "inventory.outcomes.list": ["inventory_outcomes_view", "inventory_outcomes_edit"],
  "inventory.outcomes.details": ["inventory_outcomes_view", "inventory_outcomes_edit"],
  "inventory.outcomes.create": "inventory_outcomes_edit",
  "inventory.outcomes.edit": "inventory_outcomes_edit",
  "inventory.transfers.list": ["inventory_transfers_view", "inventory_transfers_edit"],
  "inventory.transfers.details": ["inventory_transfers_view", "inventory_transfers_edit"],
  "inventory.transfers.create": "inventory_transfers_edit",
  "inventory.transfers.edit": "inventory_transfers_edit",

  // Tickets
  "tickets.list": "tickets_manage",
};

// A route's required permission can be a single permission name, a list of permission names
// (any one of them grants access), or absent (accessible to every InstitutionsAdmin).
export function hasRequiredRoutePermission(requiredPermission, userPermissions) {
  const requiredPermissions = Array.isArray(requiredPermission)
    ? requiredPermission
    : [requiredPermission].filter(Boolean);

  if (requiredPermissions.length === 0) {
    return true;
  }

  return requiredPermissions.some((permission) => userPermissions.includes(permission));
}

export const canAccessRoute = (routeName, userPermissions) =>
  hasRequiredRoutePermission(INSTITUTIONS_ADMIN_ROUTE_PERMISSIONS[routeName], userPermissions);

const checkAuthorizationForApp = async (to) => {
  const store = useStore();
  const flags = useFlags();

  // Check experimental feature flags
  const requiredFlag = EXPERIMENTAL_ROUTES[to.name];
  if (requiredFlag && !flags[requiredFlag]?.value) {
    return { name: "home" };
  }

  const rolesAllowed = to?.meta?.roles;
  const authRequired = rolesAllowed?.length > 0;
  const isAuthenticated = store.getters["auth/isAuthenticated"];

  if (authRequired && !isAuthenticated) {
    return { name: "auth.login" };
  }

  if (isAuthenticated && !store.getters["auth/user"]) {
    try {
      await store.dispatch("auth/fetchCurrentUser");
    } catch {
      // stale cross-tenant token - already logged out by the interceptor, treat as anonymous
    }
  }

  const userRoles =
    store.getters["auth/user"]?.roles || parseJWT(store.state.auth.token)?.roles || [];

  const userPermissions = store.getters["auth/permissions"] || [];

  const hasAccess =
    !rolesAllowed?.length ||
    (userRoles?.length && rolesAllowed?.findIndex((item) => userRoles.includes(item)) !== -1);

  if (!hasAccess || (isAuthenticated && to.name === "auth.login")) {
    return getDefaultRouteForRole(userRoles, userPermissions);
  }

  if (!store.getters["auth/isAdmin"] && store.getters["auth/isInstitutionsAdmin"]) {
    if (!canAccessRoute(to.name, userPermissions)) {
      return getDefaultRouteForRole(userRoles, userPermissions);
    }
  }

  return true;
};

const checkAuthorizationForAuth = async (to) => {
  const store = useStore();
  const authRequired = to?.meta?.requiresToken;
  const isAuthenticated = store.getters["auth/isAuthenticated"];

  if (authRequired && isAuthenticated) {
    return true;
  }

  if (authRequired && !isAuthenticated) {
    return { name: "auth.login" };
  }

  return true;
};

export function checkAuthorization(router) {
  router.beforeEach(IS_AUTH_BUILD_TARGET ? checkAuthorizationForAuth : checkAuthorizationForApp);
}

const DEFAULT_ROUTES_PRIORITY_FOR_INSTITUTIONS_ADMIN = [
  "dashboard",
  "orders.mealsToRelease",
  "calendar.subjects",
  "orders.list",
  "terminal",
  "parents.list",
  "children.list",
  "institutionsWorkers.list",
  "transactions",
  "mealsMenus.list",
  "kitchens.list",
  "inventory.warehouse.list",
  "inventory.dailyStockOutcomes",
  "inventory.incomes.list",
  "inventory.outcomes.list",
  "inventory.transfers.list",
  "meals.list",
  "recipes.list",
  "inventory.ingredients.list",
  "inventory.ingredientCategories.list",
  "allergens.list",
  "allergensGroups.list",
];

export const getDefaultRouteForInstitutionsAdmin = (permissions) => {
  const routeName = DEFAULT_ROUTES_PRIORITY_FOR_INSTITUTIONS_ADMIN.find((name) =>
    canAccessRoute(name, permissions),
  );

  return { name: routeName ?? "institutions.list" };
};

function getDefaultRouteForRole(userRoles, permissions = []) {
  if (userRoles.includes("ROLE_INSTITUTIONS_ADMIN")) {
    return getDefaultRouteForInstitutionsAdmin(permissions);
  }

  const defaultRoutes = {
    ROLE_COOK: "terminal",
    ROLE_PARENT: "calendar.user",
    ROLE_INSTITUTION_WORKER: "calendar.user",
    ROLE_EXTERNAL_USER: "automatedReports.list",
  };

  const defaultRouteForRole = userRoles.map((role) => defaultRoutes[role]).filter(Boolean)?.[0];

  if (defaultRouteForRole) {
    return { name: defaultRouteForRole };
  }

  return { name: "home" };
}
